import {
  normalizeTaskStats,
  sumTaskStats,
} from '../task-view/overview/overviewStats';

// counters of one project, summed over its phases
export function statsFromPhases(phases = []) {
  const workPackages = phases.flatMap((phase) => phase.workPackages || []);

  const stats = sumTaskStats(
    workPackages
      .filter((wp) => wp.status === 0 || wp.status === 2)
      .map(normalizeTaskStats),
  );

  return {
    ...stats,
    phaseCount: phases.length,
    workPackageCount: workPackages.length,
    plannedSeconds: workPackages.reduce(
      (sum, wp) => sum + (wp.estimatedTime || 0),
      0,
    ),
    workedSeconds: workPackages.reduce(
      (sum, wp) => sum + (wp.totalTimeWorkedSeconds || 0),
      0,
    ),
  };
}

// counters withStatistics=true puts on a project, null when the user may not see them
export function statsFromProject(project) {
  if (!project?.taskStats) return null;

  return {
    ...normalizeTaskStats(project),
    phaseCount: project.phasesCount || 0,
    workPackageCount: project.workPackagesCount || 0,
    plannedSeconds: project.totalTimePlannedSeconds || 0,
    workedSeconds: project.totalTimeWorkedSeconds || 0,
  };
}

// loaded phases win: they follow a ticked off task, the list response does not
export function projectCapacityStats(project, phases) {
  return phases ? statsFromPhases(phases) : statsFromProject(project);
}

export const EMPTY_STATS = {
  total: 0,
  done: 0,
  open: 0,
  overdue: 0,
  totalScore: 0,
  doneScore: 0,
  plannedSeconds: 0,
  workedSeconds: 0,
};

// the task-stats payload in the shape an overview renders
export function normalizeOverviewStats(payload) {
  const stats = payload?.taskStats || payload;
  if (!stats) return EMPTY_STATS;

  const total = stats.totalTasks || 0;
  const done = stats.doneTasks || 0;

  return {
    total,
    done,
    open: total - done,
    overdue: stats.overdueTasks || 0,
    totalScore: stats.totalEffortScore || 0,
    doneScore: stats.doneEffortScore || 0,
    plannedSeconds: stats.totalTimePlannedSeconds || 0,
    workedSeconds: stats.totalTimeWorkedSeconds || 0,
  };
}

const COUNTERS = [
  'totalTasks',
  'doneTasks',
  'overdueTasks',
  'totalEffortScore',
  'doneEffortScore',
];

// writes one changed task into the totals so the overview follows without a request
export function addTaskStatsDelta(stats, delta) {
  if (!stats) return stats;

  const target = stats.taskStats || stats;

  const updated = COUNTERS.reduce(
    (next, key) => ({ ...next, [key]: (target[key] || 0) + (delta[key] || 0) }),
    { ...target },
  );

  return stats.taskStats ? { ...stats, taskStats: updated } : updated;
}

// query of `projects?...` for a capacity style list, running projects only
export function buildCapacityProjectsQuery(
  { search, categoryIds, userIds, sortBy, sortOrder } = {},
  perPage,
) {
  const parts = ['status[eq]=0'];

  if (search?.trim()) parts.push(`search=${encodeURIComponent(search.trim())}`);

  (categoryIds || []).forEach((id) => parts.push(`categoryIds[]=${id}`));

  // the API repeats the key instead of taking a list
  (userIds || []).forEach((id) => parts.push(`userId[eq]=${id}`));

  // without a field the API sorts by project number
  if (sortBy) {
    parts.push(`sortBy=${sortBy}`);
    parts.push(`sortOrder=${sortOrder === 'desc' ? 'desc' : 'asc'}`);
  }

  if (perPage) parts.push(`perPage=${perPage}`);

  return parts.join('&');
}

// supervisor first, then the rest of the team without them a second time
export function projectTeamMembers(project) {
  if (!project) return [];

  return project.supervisor
    ? [
        project.supervisor,
        ...(project.teamMembers || []).filter(
          (user) => user.id !== project.supervisor.id,
        ),
      ]
    : project.teamMembers || [];
}
